import React, { useContext } from 'react';
import get from 'lodash/get';

import FormContext from 'src/presentation/contexts/FormContext';
import EndChat from 'src/images/EndChat.svg';
import HoverText from 'src/images/HoverText.svg';
import SSText from 'src/images/SSText.svg';
import MobileSSTextConfig from 'src/images/MobileSSTextConfig.svg';
import MobileEndChat from 'src/images/MobileEndChat.svg';
import SSTextConfig from 'src/images/SSTextConfig.svg';
import MobileThrottleMessage from 'src/images/MobileThrottleMessage.svg';

import {
  ColumnRow,
  DefaultSection,
  EndChatStyledImage,
  ExtendedSection,
  HalfRow,
  HeaderPosition,
  InputType,
  InputsBlock,
  PrimaryDescription,
  PrimaryHeader,
} from 'src/presentation/components/pages/chat/ChatLayout';

import {
  CHAT_WIDGET_TYPE,
  END_CHAT_BLOCK_INPUTS,
  FIELDS_MAPPER,
  FONT_BLOCK_INPUTS,
  HOVER_BLOCK_INPUTS,
  LABELS_BLOCK_INPUTS,
  OFFICE_HOURS_BLOCK_INPUTS,
  SDK_TEXT_DESCRIPTION,
  MOBILE_SDK_TEXT_DESCRIPTION,
  MOBILE_SELF_SERVICE_BLOCK_INPUTS,
  MOBILE_OFFICE_HOURS_BLOCK_INPUTS,
  SELF_SERVICE_BLOCK_INPUTS,
  SELF_SERVICE_WIDGET_TYPE,
} from 'src/presentation/components/pages/chat/chatConstants';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import GreetingBlock from './GreetingBlock';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import { CHAT_BRAND } from 'src/constants/branding';

const ChatTextSection = () => {
  const { errors, resetError, workingState, updateState } = useContext(FormContext);

  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Text on ${address}`;
  const isFeatureEnabled = useIsFeatureEnabled();
  const isHtmlEditorEnabled = isFeatureEnabled('gladAppHtmlEditor');

  return (
    <>
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>{SDK_TEXT_DESCRIPTION}</PrimaryDescription>
      <FontBlock workingState={workingState} />
      <LabelsBlock workingState={workingState} />
      <OfficeHoursAndThrottleMessageBlock workingState={workingState} />
      <SelfServiceChatTextBlock workingState={workingState} />
      <GreetingBlock
        workingState={workingState}
        updateState={updateState}
        errors={errors}
        resetError={resetError}
        isHtmlEditorEnabled={isHtmlEditorEnabled}
      />
      <EndChatBlock workingState={workingState} isHtmlEditorEnabled={isHtmlEditorEnabled} />
      <HoverBlock workingState={workingState} />
    </>
  );
};

export const MobileChatTextSection = () => {
  const { errors, resetError, workingState, updateState } = useContext(FormContext);

  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Text on ${address}`;
  const isFeatureEnabled = useIsFeatureEnabled();
  const isHtmlEditorEnabled = isFeatureEnabled('gladAppHtmlEditor');

  return (
    <>
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>{MOBILE_SDK_TEXT_DESCRIPTION}</PrimaryDescription>
      <OfficeHoursAndThrottleMessageBlock workingState={workingState} isMobile={true} />
      <LabelsBlock workingState={workingState} />
      <SelfServiceChatTextBlock workingState={workingState} isMobile={true} />
      <GreetingBlock
        workingState={workingState}
        updateState={updateState}
        errors={errors}
        resetError={resetError}
        isMobile={true}
        isHtmlEditorEnabled={isHtmlEditorEnabled}
      />
      <EndChatBlock workingState={workingState} isMobile={true} isHtmlEditorEnabled={isHtmlEditorEnabled} />
    </>
  );
};

const FontBlock = ({ workingState }) => {
  const widgetType = get(workingState, FIELDS_MAPPER.WIDGET_TYPE.path);

  return (
    <DefaultSection
      svgImage={SSText}
      headerPosition={HeaderPosition.INSIDE_BLOCK}
      header={widgetType === SELF_SERVICE_WIDGET_TYPE ? `${CHAT_BRAND.name} fonts` : 'General text'}
      description=""
    >
      {InputsBlock({
        workingState,
        size: InputSize.MIDDLE,
        inputType: InputType.TEXT,
        controlsList: FONT_BLOCK_INPUTS,
      })}
    </DefaultSection>
  );
};

function LabelsBlock({ workingState }) {
  const widgetType = get(workingState, FIELDS_MAPPER.WIDGET_TYPE.path);
  if (widgetType === SELF_SERVICE_WIDGET_TYPE) {
    return null;
  }

  return (
    <DefaultSection svgImage={HoverText} headerPosition={HeaderPosition.INSIDE_BLOCK} header="Labels" description="">
      {InputsBlock({
        workingState,
        size: InputSize.SMALL,
        inputType: InputType.TEXT,
        controlsList: LABELS_BLOCK_INPUTS,
      })}
    </DefaultSection>
  );
}

function OfficeHoursAndThrottleMessageBlock({ workingState, isMobile = false }) {
  const svgImage = isMobile ? MobileThrottleMessage : null;
  const imageMargin = isMobile ? 48 : 0;
  const controlsList = isMobile ? MOBILE_OFFICE_HOURS_BLOCK_INPUTS : OFFICE_HOURS_BLOCK_INPUTS;
  return (
    <DefaultSection
      imageMargin={imageMargin}
      svgImage={svgImage}
      headerPosition={HeaderPosition.INSIDE_BLOCK}
      header="Chat Unavailable Message"
      description=""
    >
      {InputsBlock({
        workingState,
        size: InputSize.XLARGE,
        inputType: InputType.TEXT_AREA,
        controlsList,
      })}
    </DefaultSection>
  );
}

function SelfServiceChatTextBlock({ workingState, isMobile = false }) {
  const widgetType = get(workingState, FIELDS_MAPPER.WIDGET_TYPE.path);
  if (widgetType === CHAT_WIDGET_TYPE) {
    return null;
  }

  let controlsList = SELF_SERVICE_BLOCK_INPUTS;
  let header = 'Self-Service & Chat';
  let description = 'Edit the header and navigation text on the Self-Service and Chat pages.';
  let svgImage = SSTextConfig;

  if (isMobile) {
    controlsList = MOBILE_SELF_SERVICE_BLOCK_INPUTS;
    header = CHAT_BRAND.name;
    description = `Assign the placeholder message in ${CHAT_BRAND.name} Chat`;
    svgImage = MobileSSTextConfig;
  }

  return (
    <DefaultSection
      svgImage={svgImage}
      headerPosition={HeaderPosition.OUTSIDE_BLOCK}
      header={header}
      description={description}
    >
      {InputsBlock({
        workingState,
        size: InputSize.MIDDLE,
        inputType: InputType.TEXT,
        controlsList: controlsList,
      })}
    </DefaultSection>
  );
}

function EndChatBlock({ workingState, isMobile = false, isHtmlEditorEnabled }) {
  let svgImage = EndChat;
  let description: any = 'Messages shown when Chat ends.';

  if (isMobile) {
    svgImage = MobileEndChat;
    description = (
      <>
        Text shown when the chat has ended. You can add links and the <br /> {'<CUSTOMERNAME>'} variable in the
        messages.
      </>
    );
  }
  const inputType = isHtmlEditorEnabled ? InputType.TEXT_AREA_HTML : InputType.TEXT_AREA;
  return (
    <ExtendedSection headerPosition={HeaderPosition.OUTSIDE_BLOCK} header="End chat" description={description}>
      <HalfRow>
        <ColumnRow>
          {InputsBlock({
            workingState,
            size: InputSize.XLARGE,
            inputType: inputType,
            controlsList: END_CHAT_BLOCK_INPUTS,
          })}
        </ColumnRow>
      </HalfRow>
      <HalfRow>
        <EndChatStyledImage src={svgImage} />
      </HalfRow>
    </ExtendedSection>
  );
}

function HoverBlock({ workingState }) {
  return (
    <DefaultSection
      svgImage={HoverText}
      headerPosition={HeaderPosition.INSIDE_BLOCK}
      header="Hover text"
      description="Texts that appear when customers hover over navigation links and icons."
    >
      {InputsBlock({
        workingState,
        size: InputSize.SMALL,
        inputType: InputType.TEXT,
        controlsList: HOVER_BLOCK_INPUTS,
      })}
    </DefaultSection>
  );
}

export default ChatTextSection;
